import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import AuthContext from '../../../context/authContext';
import usePendingBookings from '../../../hooks/usePendingBookings';
import ResolutionResponsibleFields, {
  REASON_MIN_LENGTH,
} from './ResolutionResponsibleFields';

/**
 * Action for the "forgottenPayment" pending-attention case: customer actually paid
 * on site but staff forgot to mark it. Resolves the case by flipping offline
 * payment references to paid, with mandatory reason + responsible-person audit
 * trail.
 */
function ResolveForgottenPaymentButton({
  booking,
  onBookingUpdated,
  variant = 'contained',
  size = 'small',
  fullWidth = false,
  sx = undefined,
}) {
  const authCtx = useContext(AuthContext);
  const { resolveForgottenPayment, fetchResolutionCandidates } = usePendingBookings();

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [responsibleUserId, setResponsibleUserId] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [candidatesError, setCandidatesError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canShow = useMemo(() => {
    if (!booking?._id) return false;
    if (!authCtx.checkRightAny('internalBooking:special:resolveForgotten')) return false;
    if (booking.status !== 'confirmed') return false;
    if (!booking.presentSince) return false;
    if (booking.fullyPaid === true) return false;
    return true;
  }, [authCtx, booking]);

  // Keep the latest fetcher in a ref so the effect below only re-runs on
  // open/booking changes — never on a mere identity change of the hook callback.
  const fetchResolutionCandidatesRef = useRef(fetchResolutionCandidates);
  fetchResolutionCandidatesRef.current = fetchResolutionCandidates;

  useEffect(() => {
    if (!open || !booking?._id) return undefined;
    let cancelled = false;
    setIsLoadingCandidates(true);
    setCandidatesError(null);
    fetchResolutionCandidatesRef
      .current(booking._id)
      .then(({ candidates: loaded }) => {
        if (cancelled) return;
        setCandidates(loaded);
        setResponsibleUserId(prev => {
          if (loaded.length === 1) return loaded[0].userId;
          if (prev && loaded.some(c => c.userId === prev)) return prev;
          return null;
        });
      })
      .catch(err => {
        if (cancelled) return;
        setCandidatesError(err);
        setCandidates([]);
        setResponsibleUserId(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingCandidates(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, booking?._id]);

  if (!canShow) return null;

  const handleOpen = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setOpen(true);
  };

  const handleClose = event => {
    event?.stopPropagation?.();
    if (isSubmitting) return;
    setOpen(false);
    setReason('');
  };

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= REASON_MIN_LENGTH;
  const responsibleValid = candidates.length <= 1 || Boolean(responsibleUserId);
  const submitDisabled =
    isSubmitting || isLoadingCandidates || !reasonValid || !responsibleValid;

  const handleSubmit = async event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!booking?._id || submitDisabled) return;

    try {
      setIsSubmitting(true);
      const toastMessage = `Die Zahlungsbestätigung für ${booking.firstName} ${booking.lastName} wurde gespeichert und kann nach Identifizierung abgeschlossen werden`;
      const successToastMessage = 'Als vollständig bezahlt markiert (Vergessen-Auflösung)';
      const displayInfo = {
        Typ: 'Vergessene Zahlung auflösen',
        Kunde: `${booking.firstName} ${booking.lastName}`,
        Buchungsnummer: booking.bookingNumber,
        Termin: dayjs(booking.startTime).format('DD.MM.YYYY HH:mm'),
        Grund: trimmedReason,
      };
      const onSuccess = updated => {
        if (updated && typeof onBookingUpdated === 'function') {
          onBookingUpdated(booking._id, updated);
        }
      };
      const updatedBooking = await resolveForgottenPayment(
        booking._id,
        { reason: trimmedReason, responsibleUserId: responsibleUserId || null },
        toastMessage,
        null,
        displayInfo,
        onSuccess,
        successToastMessage
      );
      if (updatedBooking && typeof onBookingUpdated === 'function') {
        onBookingUpdated(booking._id, updatedBooking);
      }
      setOpen(false);
      setReason('');
    } catch (_) {
      // Errors are handled by the hook/useHttp via toast.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        color='primary'
        fullWidth={fullWidth}
        onClick={handleOpen}
        data-ignore-card-click
        sx={{ whiteSpace: 'nowrap', ...sx }}
      >
        Als bezahlt markieren
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
        <DialogTitle>Vergessene Zahlung auflösen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Der Kunde war vor Ort und hat <strong>tatsächlich bezahlt</strong>, es wurde nur
              versäumt, die Zahlung im System zu markieren. Mit dieser Aktion werden alle
              offenen Offline-Zahlungen der Buchung als bezahlt gekennzeichnet. Sollte der
              Kunde <em>nicht</em> bezahlt haben, bitte stattdessen „Rechnung per Mail senden"
              verwenden.
            </Typography>

            <ResolutionResponsibleFields
              candidates={candidates}
              isLoadingCandidates={isLoadingCandidates}
              candidatesError={candidatesError}
              responsibleUserId={responsibleUserId}
              onResponsibleChange={setResponsibleUserId}
              reason={reason}
              onReasonChange={setReason}
              disabled={isSubmitting}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={submitDisabled}
          >
            Als bezahlt markieren
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ResolveForgottenPaymentButton;
